import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, IconButton, Switch } from '@mui/material';
import { formatDistanceToNow, isToday, isYesterday, isThisWeek, isAfter, startOfYesterday } from 'date-fns';
import { Bell, X, CheckCircle } from 'lucide-react';

import { CustomEvents, JoinTypes, NotificationTypes } from '@labradorsports/constants';

import { setNotificationBadge } from '../../app/plugins/notifications/index.js';
import { OnboardingStatusCard } from '../../dashboard/index.js';
import { Panel, PanelHeader, useLogger, useNavigateWithQuery } from '../../shared/index.js';
import { SafeDateFormat } from '../../shared/structural/index.js';
import {
    Selectors,
    useGetNotificationsQuery,
    useLoadRosterQuery,
    useMarkNotificationsReadMutation,
} from '../../store/index.js';

interface Props {
    onClose: () => void;
    visible: boolean;
}

const Notifications: FC<Props> = ({ onClose, visible }) => {
    const navigate = useNavigateWithQuery();
    const logger = useLogger();
    const uid = useSelector(Selectors.uid);
    const teams = useSelector(Selectors.allTeams);
    const currentTeam = useSelector(Selectors.currentTeam);
    const userRole = useSelector(Selectors.currentUserTeamRole);

    const [filterMode, setFilterMode] = useState<'all' | 'team'>('all');

    const { data: notifications = [], refetch } = useGetNotificationsQuery(
        { uid },
        { skip: !uid, pollingInterval: 5 * 60 * 1000, skipPollingIfUnfocused: false }
    );
    const [markNotificationsRead] = useMarkNotificationsReadMutation();
    const { data: { playerRoster } = {} } = useLoadRosterQuery();

    const joinedToday = playerRoster.filter(
        (player) => player.joinedAt && isAfter(player.joinedAt, startOfYesterday())
    );

    // Filter notifications based on the selected mode
    const filteredNotifications = notifications.filter((notification) => {
        if (!notification.contents) {
            return false;
        }

        if (filterMode === 'all') {
            return true;
        }

        // For team mode, show notifications that either:
        // 1. Have no teamId (global notifications)
        // 2. Have a teamId that matches the current team
        const notificationTeamId = notification.contents.data?.teamId;
        return !notificationTeamId || notificationTeamId === currentTeam?.id;
    });

    useEffect(() => {
        const handler = () => {
            refetch();
        };

        window.addEventListener(CustomEvents.PUSH_NOTIFICATION_TAP, handler);

        return () => {
            window.removeEventListener(CustomEvents.PUSH_NOTIFICATION_TAP, handler);
        };
    }, []);

    useEffect(() => {
        if (uid && notifications.length > 0) {
            setNotificationBadge(logger, notifications.filter((notification) => !notification.read).length);
        }

        return () => {
            // Mark all unread notifications as read when the panel is closed
            if (visible && uid && notifications.length > 0) {
                const unreadNotificationIds = notifications
                    .filter((notification) => !notification.read)
                    .map((notification) => notification.id) // Use the actual notification ID
                    .filter(Boolean); // Filter out any undefined IDs

                if (unreadNotificationIds.length > 0) {
                    markNotificationsRead({ uid, notificationIds: unreadNotificationIds });
                    if (APP) {
                        setNotificationBadge(logger, 0);
                    }
                }
            }
        };
    }, [uid, notifications, visible]);

    const formatDate = (date: Date) => {
        // Check if the date is valid
        if (!date || isNaN(date.getTime())) return '';

        // Use date-fns for more accurate and readable formatting
        if (isToday(date)) {
            return formatDistanceToNow(date, { addSuffix: true });
        } else if (isYesterday(date)) {
            return 'Yesterday';
        } else if (isThisWeek(date)) {
            return formatDistanceToNow(date, { addSuffix: true });
        } else {
            return <SafeDateFormat date={date} pattern="MMM d, yyyy" />;
        }
    };

    const notificationClick = (notification) => {
        const { data = {} } = notification.contents;

        onClose();

        switch (notification.type) {
            case NotificationTypes.TEAM_PLAYS_ADDED:
            case NotificationTypes.UNVIEWED_PLAYS:
                navigate('/playbook/team', {
                    activeTeam: data.teamId,
                    playbookFilter: {
                        unviewedOnly: true,
                    },
                });
                break;

            case NotificationTypes.STUDY_REMINDER:
                navigate('/playbook/team', {
                    activeTeam: data.teamId,
                    playId: data.playId,
                });
                break;

            case NotificationTypes.EVENT_ADDED:
            case NotificationTypes.UPCOMING_EVENT:
            case NotificationTypes.EVENTS_UPDATED:
            case NotificationTypes.EVENT_STARTING_SOON:
                navigate('/schedule/event/details', {
                    activeTeam: data.teamId,
                    viewEventId: data.eventId,
                });
                break;

            case NotificationTypes.SCOUTING_REPORT_ADDED:
                navigate('/schedule/event/scouting-report', {
                    activeTeam: data.teamId,
                    viewEventId: data.eventId,
                });
                break;

            case NotificationTypes.PRACTICE_PLAN_ADDED:
                navigate('/schedule/event/practice-plan', {
                    activeTeam: data.teamId,
                    viewEventId: data.eventId,
                });
                break;

            case NotificationTypes.EVALUATION_ADDED:
            case NotificationTypes.EVALUATION_UPDATED:
                navigate('/profile/account/evaluations', {
                    playerEvaluationId: data.evaluationId,
                });
                break;

            case NotificationTypes.EVALUATION_WITHDRAWN:
                navigate('/profile/account/evaluations');
                break;

            case NotificationTypes.DOCUMENT_SHARED:
            case NotificationTypes.DOCUMENT_ACK_REQUEST:
                navigate('/documents', {
                    activeTeam: data.teamId,
                });
                break;

            case NotificationTypes.POST_ADDED:
            case NotificationTypes.POST_RESPONSE:
                navigate('/feed', {
                    activeTeam: data.teamId,
                    viewPostId: data.postId,
                });
                break;
        }
    };

    const teamsMap = teams.reduce((acc, team) => {
        acc[team.id] = team;
        return acc;
    }, {});

    const showOnboarding = joinedToday.length > 0 && userRole !== JoinTypes.PLAYER;
    const showNotifications = filteredNotifications.length > 0 || showOnboarding;

    return (
        <Panel
            header={
                <PanelHeader title="Notifications">
                    <IconButton className="close-button" onClick={onClose}>
                        <X className="icon" />
                    </IconButton>
                    {showNotifications ? (
                        <div className="pt-2 d-flex align-items-center">
                            <div className="filter-toggle flex-grow-1">
                                <span className="filter-label">All</span>
                                <Switch
                                    checked={filterMode === 'team'}
                                    onChange={(event) => setFilterMode(event.target.checked ? 'team' : 'all')}
                                    size="small"
                                />
                                <span className="filter-label">Team Only</span>
                            </div>
                            <Button onClick={() => navigate('/profile/account/notifications')}>Manage</Button>
                        </div>
                    ) : null}
                </PanelHeader>
            }
            onRefresh={refetch}
            className={`notifications${visible ? ' visible' : ''}`}
            content={
                <div className="notifications-content">
                    {showNotifications ? (
                        <div className="notifications-list">
                            <OnboardingStatusCard />
                            {filteredNotifications.map((notification, index) => (
                                <div
                                    key={index}
                                    className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                                    onClick={() => notificationClick(notification)}
                                >
                                    <div className="notification-content">
                                        <div className="notification-header">
                                            <div className="notification-title">
                                                <h3>{notification.contents?.title || 'Notification'}</h3>
                                                {notification.contents.data?.teamId ? (
                                                    <div className="small notification-team">
                                                        {teamsMap[notification.contents.data.teamId]?.name}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div className="notification-meta">
                                                <span className="notification-time">
                                                    {formatDate(notification.createdAt)}
                                                </span>
                                                {notification.read ? (
                                                    <CheckCircle className="read-icon" />
                                                ) : (
                                                    <div className="unread-indicator" />
                                                )}
                                            </div>
                                        </div>

                                        <p className="notification-body">{notification.contents.body}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <Bell className="empty-icon" />
                            <p>No notifications yet</p>
                            <span>You'll see updates about your team and activities here</span>
                        </div>
                    )}
                </div>
            }
        />
    );
};

export default Notifications;
